import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { BACKEND_URL, KEY_CONFIG_URL, REFRESH_URL } from '../Helpers/constants';
import { IGetEventsAPIResponse, IGetEventsResponse } from '../interfaces/IDashBoard';
import { ILoginApiPayload, ILoginApiResponse, ISignUpPayload } from '../interfaces/ILogin';
import { ICustomerPayment } from '../interfaces/IStepperForm';
import { LoginData, VerifySignUpFormData } from '../interfaces/Auth';
import { Images } from '../Assets/banner';

export let axiosInstance: AxiosInstance;
const configUrl = KEY_CONFIG_URL;

// ─── Device fingerprint ───────────────────────────────────────────────────────
// Generated once per browser, stored in localStorage, sent via x-device-id on
// every request. Bound into the JWT at login; mismatches trigger a 401 (CWE-384).
const getDeviceId = (): string => {
  const key = 'x-device-id';
  const stored = localStorage.getItem(key);
  if (stored) return stored;
  const id: string = (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function')
    ? (crypto as any).randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(key, id);
  return id;
};

export const logoutUser = async (): Promise<void> => {
  try {
    await axios.post(`${BACKEND_URL}/v1/auth/logout`, {}, { withCredentials: true });
  } catch { /* best-effort */ }
  sessionStorage.removeItem('user');
};

// Shared refresh-in-progress state — prevents duplicate refresh calls when
// multiple requests fail with 401 at the same time.
let isRefreshing = false;
let pendingRequests: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

const flushQueue = (error?: unknown): void => {
  pendingRequests.forEach(p => (error ? p.reject(error) : p.resolve()));
  pendingRequests = [];
};

export const getAxiosInstance = (_data?: { config?: { apiBaseUrl: string; tokenUrl: string } }) => {
  axiosInstance = axios.create({
    baseURL: BACKEND_URL,
    timeout: 15000,
    withCredentials: true,    // sends HttpOnly cookies (access_token / refresh_token) cross-origin
  });

  // Attach device fingerprint to every request so the backend can enforce CWE-384.
  axiosInstance.interceptors.request.use((req: AxiosRequestConfig): any => {
    req.headers = req.headers || {};
    req.headers['x-device-id'] = getDeviceId();
    return req;
  });

  // 401 → rotate the refresh token (HttpOnly cookie), then retry once.
  axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const originalRequest = error.config;
      if (!error.response || error.response.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the in-flight refresh completes.
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: () => resolve(axiosInstance(originalRequest)),
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // The browser automatically sends the HttpOnly refresh_token cookie here.
        await axios.post(REFRESH_URL, {}, {
          withCredentials: true,
          headers: { 'x-device-id': getDeviceId() },
        });
        flushQueue();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        flushQueue(refreshError);
        await logoutUser();
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export const getConfig = async () => {
  try {
    // const path = 'infive-application-stg.kennovate.com';
    // const path = '66581d31a926bb001ae681f4';
    // const res = await axios.get(`${configUrl}/${path}`);

    // if(res && res.data){
    //   res.data.config.apiBaseUrl = "http://localhost:4000";
    //   // res.data[0]
    // }

    return null;
  } catch (err) {
    return err;
  }
};

/*
*
*
Axios Instance Creation
*
*
*/

const AppInstance = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

AppInstance.interceptors.request.use((req: AxiosRequestConfig): any => {
  req.headers = req.headers || {};
  req.headers['x-device-id'] = getDeviceId();
  return req;
});

AppInstance.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const originalRequest = error.config;
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: () => resolve(AppInstance(originalRequest)),
          reject,
        });
      });
    }
    originalRequest._retry = true;
    isRefreshing = true;
    try {
      await axios.post(REFRESH_URL, {}, {
        withCredentials: true,
        headers: { 'x-device-id': getDeviceId() },
      });
      flushQueue();
      return AppInstance(originalRequest);
    } catch (refreshError) {
      flushQueue(refreshError);
      await logoutUser();
      window.location.href = '/';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/*
*
*
Authentication API's
*
*
*/

export const loginSSO = async (email: string) => {
  try {
    const path = `/v1/auth/sso/user/verify?email=${email}`;
    const response = axiosInstance.get(path);
    return response;
    return;
  } catch (error) {
    return error;
  }
};

// Login (POST)

// export const login: (payload: ILoginApiPayload) => Promise<AxiosResponse<ILoginApiResponse>> = (payload: ILoginApiPayload) => {
//   const getLoginResponse = AppInstance.post('v1/login', payload);
//   return getLoginResponse;
// };

export const authLogin = async (data: LoginData) => {
  try {
    const path = `v1/auth/login/verify`;
    const res: any = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

// GET OTP

export const getOtp = async (email: string, reset: boolean) => {
  const path = `/v1/auth/send/otp?email=${email}&update=${reset}`;
  const res = await axiosInstance.post(path);
  console.log(res.data, 'dataRas');
  return res && res.data ? res.data : res;
};

// Verify OTP

export const verifyOtp = async (data: VerifySignUpFormData) => {
  try {
    const path = `/v1/auth/verify/otp`;
    const res = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

// Reset OTP

export const verifyOtpReset = async (email: string, otp: string) => {
  try {
    const path = `/v1/auth/verify/otpwithContact?email=${email}&otp=${otp}`;
    const res = await axiosInstance.post(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

// AccessToken (POST)

export const getAccessToken: (refreshToken: string) => Promise<AxiosResponse<ILoginApiResponse>> = (refreshToken: string) => {
  const payload = {
    refreshToken: refreshToken,
    portal: 'application-portal',
  };
  const getRefresh = AppInstance.post('v1/refreshToken', payload);
  return getRefresh;
};

// Logout (POST)

export const logout: (refreshToken: string) => Promise<AxiosResponse<ILoginApiResponse>> = (refreshToken: string) => {
  const payload = {
    refreshtoken: refreshToken,
  };
  const logout = AppInstance.post('v1/logout', payload);
  return logout;
};

// Reset Password (POST)

export const resetPassword = async (data: any) => {
  try {
    const path = `/v1/auth/login/reset`;
    const res: any = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

/**
 *
 *
 *
 * New Authentication
 *
 *
 * */

// export const getOtpWithoutContact = (email: string) => {
//   const res = AppInstance.post(`v1/otpwithoutsaving?email=${email}`);
//   return res;
// };

export const verifyOtpWithoutContact = (email: string, otp: string) => {
  const res = AppInstance.get(`v1/verifyotpwithoutcontact?contact=${email}&otpEnteredEmail=${otp}`);
  return res;
};

// export const getOtp = (email: string) => {
//   const res = AppInstance.post(`v1/signupotp?contact=${email}`);
//   return res;
// };

// export const verifyOtp = (email: string, otp: string) => {
//   const res = AppInstance.get(`v1/verifyotp?contact=${email}&otpentered=${otp}`);
//   return res;
// };

export const saveContact = (payload: ISignUpPayload) => {
  const res = AppInstance.post('v1/application/startup', { applicant: payload });
  return res;
};

/*
*
*
Axios Interceptor Configuration
*
*
*/

/** @deprecated Tokens are now managed as HttpOnly cookies. Returns null. */
export const ApiConfig: () => null = () => null;

/*
*
*
Dashboard
*
*
*/

// Get Events (GET)

export const getEvents: () => Promise<AxiosResponse<IGetEventsAPIResponse<Array<IGetEventsResponse>>>> = async () => {
  const getData = await AppInstance.get('v1/application/application/startup/forms');
  let customData = {...getData};
  customData.data.result.data.forEach((items: any) => {
    switch (items?.industryType) {
      case 'Tech':
        items['program']['bannerImage'] = Images.techBanner;
        items['program']['cardImage'] = Images.techCard;
        return { ...items };
      case 'Media':
        items['program']['bannerImage'] = Images.mediaBanner;
        items['program']['cardImage'] = Images.mediaCard;
        return { ...items };
      case 'Design':
        items['program']['bannerImage'] = Images.designBanner;
        items['program']['cardImage'] = Images.designCard;
        return { ...items };
      case 'Science':
        items['program']['bannerImage'] = Images.scienceBanner;
        items['program']['cardImage'] = Images.scienceCard;
        return { ...items };
      default:
        return { ...items };
    }
  });
  return customData;
};

//Get Event Details (GET)

export const getEventDetails: (id: string) => Promise<AxiosResponse<IGetEventsAPIResponse<IGetEventsResponse>>> = async (id: string) => {
  const getDetails = await AppInstance.get('v1/application/application/startup/formsbyid', {
    params: {
      formid: id,
    },
  });
  let customDetails: any = { ...getDetails };
  switch (customDetails.data.result?.data?.industryType) {
    case 'Tech':
      customDetails.data.result.data['program']['bannerImage'] = Images.techBanner;
      customDetails.data.result.data['program']['cardImage'] = Images.techCard;
      return { ...customDetails };
    case 'Media':
      customDetails.data.result.data['program']['bannerImage'] = Images.mediaBanner;
      customDetails.data.result.data['program']['cardImage'] = Images.mediaCard;
      return { ...customDetails };
    case 'Design':
      customDetails.data.result.data['program']['bannerImage'] = Images.designBanner;
      customDetails.data.result.data['program']['cardImage'] = Images.designCard;
      return { ...customDetails };
    case 'Science':
      customDetails.data.result.data['program']['bannerImage'] = Images.scienceBanner;
      customDetails.data.result.data['program']['cardImage'] = Images.scienceCard;
      return { ...customDetails };
    default:
      return { ...customDetails };
  }
};

// GET Pending Forms (GET)

export const getPendingForms: (applicantId: string) => Promise<AxiosResponse> = async (applicantId: string) => {
  const getPendingData = await AppInstance.get('/v1/application/startup/application/applicant', {
    params: {
      applicantid: applicantId,
    },
  });
  return getPendingData;
};

// Get Pending Form Details

export const getPendingFormDetails: (formId: string) => Promise<AxiosResponse> = async (formId: string) => {
  const getPendingFormDetails = await AppInstance.get('v1/application/startup/application', {
    params: {
      applicationid: formId,
    },
  });
  return getPendingFormDetails;
};

// Get Pending Applications
export const getPendingApplications: (accountName: string) => Promise<AxiosResponse> = async (accountName: string) => {
  const getPendingApplications = await AppInstance.get('v1/application/pending/appllication', {
    params: {
      accountName: accountName,
    },
  });
  return getPendingApplications;
};

/*
*
*
Stepper Form
*
*
*/

// Post Event Application (POST)

export const postEventApplication: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const postData = await AppInstance.post('v1/application/application/startup', payload, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
  return postData;
};

// Get Attachements Array List (GET)

// export const getAttachementsList: (formId: string) => Promise<AxiosResponse> = async (formId: string) => {
//   const getAttachements = await AppInstance.get('v1/application/startup/applicationdocuments', {
//     params: {
//       applicationid: formId,
//     },
//   });
//   return getAttachements;
// };

// Get Documents By ID (GET)

export const getDocuments: (recordId: string) => Promise<AxiosResponse> = async (recordId: string) => {
  const getDocuments = await AppInstance.get('v1/application/startup/applicationdocuments/docid', {
    params: {
      docid: recordId,
    },
  });
  return getDocuments && getDocuments.data;
};

export const postInterviewSlots: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const result = await AppInstance.post('v1/application/startup/interviewSlots', payload, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
  return result && result?.data;
};

export const postYesBankInitiatePayment: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const result = await AppInstance.post('v1/initiate/yesbank/payment', payload, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
  return result && result?.data;
};

export const getInterviewSlots: (formId: string) => Promise<AxiosResponse> = async (formId: string) => {
  const result = await AppInstance.get('v1/application/startup/interviewSlots', {
    params: {
      formId: formId,
    },
  });
  return result && result?.data;
};

// export const getDocumentsViaRecordId: (documentId: string) => Promise<AxiosResponse> = async (documentId: string) => {
//   const result = await AppInstance.get('/v1/application/retrieve/document', {
//     params: {
//       documentId: documentId,
//     },
//   });
//   return result && result?.data;
// };

// Get FAQs (GET)

export const getFAQs: () => Promise<AxiosResponse> = async () => {
  const result = await AppInstance.get('/v1/application/faq');
  return result;
};

// Payment VIA Razorpay (POST)

export const postPayment: (amount: number, currency: string, event: string, customer: ICustomerPayment, formId: string, callbackUrl: string) => Promise<AxiosResponse> = async (
  amount: number,
  currency: string,
  event: string,
  customer: ICustomerPayment,
  formId: string,
  callbackUrl: string
) => {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const contactId = user.applicantId;
  const getPaymentSucces = await AppInstance.post('v1/generatepaymentlink', {
    amount: amount,
    currency: currency,
    description: event,
    customer: customer,
    notify: {
      sms: true,
      email: true,
    },
    reminder_enable: true,
    notes: {
      participateid: '',
      applicationid: formId,
      contactid: contactId,
      RecordTypeId: 'Application Transaction',
      policy_name: 'Jeevan Bima',
    },
    callback_url: callbackUrl,
    callback_method: 'get',
  });
  return getPaymentSucces;
};
